import { IUserLevelCoupon } from "@/api_framework/api_modals/coupon";
import Input from "@/app/components/ui/Inputs/Input/index";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCouponDetailsAPI } from "@/app/hooks/api_hooks/coupon/useCouponDetailsAPI";
import { useBaseCouponSearchAPI } from "@/app/hooks/api_hooks/user_level_coupon/useBaseCouponSearchAPI";
import { useCouponApplicableItemsAPI } from "@/app/hooks/api_hooks/user_level_coupon/useCouponApplicableItemsAPI";
import { useCreateUserLevelCouponAPI } from "@/app/hooks/api_hooks/user_level_coupon/useCreateUserLevelCouponAPI";
import { useCustomerLookupAPI } from "@/app/hooks/api_hooks/user_level_coupon/useCustomerLookupAPI";
import { useUserLevelCouponListAPI } from "@/app/hooks/api_hooks/user_level_coupon/useUserLevelCouponListAPI";
import { useUserLevelCouponReasonsAPI } from "@/app/hooks/api_hooks/user_level_coupon/useUserLevelCouponReasonsAPI";
import { Copy, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

const formatDiscount = (type: string | null, value: number | null) => {
  if (value === null || value === undefined) return "-";
  return type === "PERCENTAGE" ? `${value}%` : `₹${value}`;
};

const copyCode = (code: string) => {
  navigator.clipboard?.writeText(code);
  toast.success(`Coupon ${code} copied`);
};

const UserLevelCouponPage = () => {
  const { register, watch, setValue } = useForm<{ userId: string; base: string; reason: string }>({
    defaultValues: { userId: "" },
  });
  const [selectedBase, setSelectedBase] = useState<{ coupon_id: string; coupon_name: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectorKey, setSelectorKey] = useState(0); // bump to remount + clear the base-coupon search
  const [showItems, setShowItems] = useState(false);
  const [itemQuery, setItemQuery] = useState("");
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const userId = watch("userId") ?? "";

  const REASON_NOTE_MIN = 25;

  const { execute: lookupCustomer, customer, setCustomer } = useCustomerLookupAPI();
  const { execute: searchBaseCoupons, baseCoupons } = useBaseCouponSearchAPI();
  const { execute: fetchCouponDetails, couponDetails } = useCouponDetailsAPI();
  const { execute: createCoupon } = useCreateUserLevelCouponAPI();
  const { execute: fetchList, coupons } = useUserLevelCouponListAPI();
  const { execute: fetchApplicableItems, items: applicableItems, loading: itemsLoading } = useCouponApplicableItemsAPI();
  const { execute: fetchReasons, reasons } = useUserLevelCouponReasonsAPI();

  useEffect(() => {
    fetchList();
    searchBaseCoupons("");
    fetchReasons();
  }, []);

  // Stable reference — an inline .map() each render makes SearchableSelectMenu's
  // selectItems-effect loop forever.
  const baseCouponItems = useMemo(
    () => baseCoupons.map((c) => ({ id: c.coupon_id, title: c.coupon_name })),
    [baseCoupons],
  );

  const reasonItems = useMemo(() => reasons.map((r) => ({ id: r, title: r })), [reasons]);

  const filteredGroups = useMemo(() => {
    const groups = applicableItems?.groups ?? [];
    const q = itemQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) =>
        g.all_inclusive ? g : { ...g, items: g.items.filter((i) => (i.name || i.id).toLowerCase().includes(q)) },
      )
      .filter((g) => g.all_inclusive || g.items.length > 0);
  }, [applicableItems, itemQuery]);

  const selectedDiscount = (() => {
    if (!couponDetails) return "-";
    if (couponDetails.discount_level === "ITEM") {
      const price = couponDetails.item_discount_details?.included_details?.price;
      return formatDiscount(price?.discount_price_type ?? null, price?.discount_value ?? null);
    }
    const cart = couponDetails.cart_discount_details;
    return formatDiscount(cart?.discount_type ?? null, cart?.discount_value ?? null);
  })();

  const handleLookup = () => {
    if (userId.trim()) lookupCustomer(userId.trim());
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    const res = await createCoupon({
      base_coupon_id: selectedBase!.coupon_id,
      user_id: userId.trim(),
      reason,
      reason_note: reasonNote.trim(),
    });
    setCreating(false);
    if (res?.status && res.data) {
      copyCode(res.data.coupon_code);
      setValue("userId", "");
      setCustomer(null);
      setSelectedBase(null);
      setReason("");
      setReasonNote("");
      setShowItems(false);
      setSelectorKey((k) => k + 1); // clears the base-coupon search box
      fetchList();
    }
  };

  const columns = [
    { header: "Created On", accessor: "created_at" as const },
    {
      header: "Base Coupon",
      cell: (row: IUserLevelCoupon) =>
        `${row.base_coupon_name} (${formatDiscount(row.discount_summary?.discount_type, row.discount_summary?.discount_value)})`,
    },
    {
      header: "Coupon Code",
      cell: (row: IUserLevelCoupon) => (
        <button className="flex items-center gap-1 text-[#193A6A] font-medium" onClick={() => copyCode(row.coupon_code)}>
          {row.coupon_code} <Copy size={12} />
        </button>
      ),
    },
    {
      header: "Customer",
      cell: (row: IUserLevelCoupon) => (
        <div>
          <p className="font-medium">{row.user_name || "-"}</p>
          <p className="text-gray-500">{[row.user_phone, row.user_email].filter(Boolean).join(" · ") || row.user_id}</p>
        </div>
      ),
    },
    {
      header: "Reason",
      cell: (row: IUserLevelCoupon) => (
        <div className="max-w-[220px]">
          <p className="font-medium">{row.reason || "-"}</p>
          {row.reason_note && <p className="text-gray-500 whitespace-pre-wrap break-words">{row.reason_note}</p>}
        </div>
      ),
    },
    { header: "Valid Till", accessor: "valid_till" as const },
  ];

  const canCreate = Boolean(
    userId.trim() && selectedBase && customer && reason && reasonNote.trim().length >= REASON_NOTE_MIN,
  );

  return (
    <div className="p-4 bg-white">
      <h2 className="text-sm font-bold text-gray-800 tracking-wide uppercase mb-4">User Level Coupon</h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleCreate();
        }}
        className="flex flex-col gap-3 border border-gray-100 rounded-lg p-4 bg-gray-50/50 max-w-5xl"
      >
        <div className="flex flex-col lg:flex-row lg:items-start gap-3">
          <div className="w-full lg:flex-1">
            <p className="text-sm font-semibold text-gray-700 mb-1">Customer User ID</p>
            <Input
              register={register("userId")}
              fieldError={undefined}
              onBlur={handleLookup}
              placeholder="Enter customer user id"
              errorMessages={[]}
              disableFieldError
            />
            {customer && (
              <p className="text-xs text-gray-600 mt-1">
                {customer.name} · {customer.mobile}
              </p>
            )}
          </div>

          <div className="w-full lg:flex-1">
            <p className="text-sm font-semibold text-gray-700 mb-1">Base Coupon</p>
            <SearchableSelectMenu
              key={`base-${selectorKey}`}
              errorMessages={[]}
              fieldError={undefined}
              placeholder="Search base coupon"
              selectItems={baseCouponItems}
              onInputChange={(q: string) => searchBaseCoupons(q)}
              onSelectItem={(item) => {
                if (item?.id) {
                  setSelectedBase({ coupon_id: item.id, coupon_name: item.title });
                  fetchCouponDetails(item.id);
                  fetchApplicableItems(item.id);
                }
              }}
              showDropdownIcon
              showDropdownWhenNoData
              register={register("base")}
              containerClassName="w-full"
            />
            {selectedBase && (
              <p className="text-xs text-gray-600 mt-1">
                Discount: {selectedDiscount}
                <button
                  type="button"
                  onClick={() => {
                    setItemQuery("");
                    setShowItems(true);
                  }}
                  className="ml-2 text-[#193A6A] font-medium underline"
                >
                  View eligible items
                </button>
              </p>
            )}
          </div>

          <div className="w-full lg:w-24">
            <p className="text-sm font-semibold text-gray-700 mb-1">Valid Till</p>
            <p className="text-xs text-gray-600 py-2">Today</p>
          </div>

        </div>

        <div className="flex flex-col lg:flex-row lg:items-start gap-3">
          <div className="w-full lg:flex-1">
            <p className="text-sm font-semibold text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </p>
            <SearchableSelectMenu
              key={`reason-${selectorKey}`}
              errorMessages={[]}
              fieldError={undefined}
              placeholder="Select reason"
              selectItems={reasonItems}
              onSelectItem={(item) => setReason(item?.id ?? "")}
              showDropdownIcon
              showDropdownWhenNoData
              register={register("reason")}
              containerClassName="w-full"
            />
          </div>

          <div className="w-full lg:flex-[2]">
            <p className="text-sm font-semibold text-gray-700 mb-1">
              Remarks <span className="text-red-500">*</span>
            </p>
            <textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              rows={2}
              placeholder={`Add remarks for RCA (min ${REASON_NOTE_MIN} characters)`}
              className="w-full text-[10px] sm:text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 text-black focus:ring-0 focus:outline-none"
            />
            <p className="text-[11px] text-gray-500 mt-0.5">
              {reasonNote.trim().length}/{REASON_NOTE_MIN} characters minimum
            </p>
          </div>

          <div className="shrink-0">
            <p className="text-sm font-semibold mb-1 invisible" aria-hidden="true">.</p>
            <button
              type="submit"
              disabled={!canCreate || creating}
              className="text-xs h-[34px] inline-flex items-center justify-center rounded-md bg-[#193A6A] px-5 font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </form>

      <DataTable data={coupons} columns={columns} noDataMessage="No coupons created today" />

      {showItems && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowItems(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800">Eligible items · {selectedBase?.coupon_name}</h3>
              <button type="button" onClick={() => setShowItems(false)} className="text-gray-500 hover:text-gray-800">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 border-b border-gray-100">
              <input
                value={itemQuery}
                onChange={(e) => setItemQuery(e.target.value)}
                placeholder="Search items"
                className="w-full text-xs rounded-md ring-1 ring-inset ring-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-600"
              />
            </div>
            <div className="p-4 overflow-y-auto">
              {itemsLoading ? (
                <p className="text-xs text-gray-500">Loading…</p>
              ) : filteredGroups.length === 0 ? (
                <p className="text-xs text-gray-500">No applicable items</p>
              ) : (
                filteredGroups.map((group) => (
                  <div key={group.type} className="mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-1">
                      {group.type} {group.all_inclusive ? "(All)" : `(${group.items.length})`}
                    </p>
                    {group.all_inclusive ? (
                      <p className="text-xs text-gray-500">Applies to all</p>
                    ) : (
                      <ul className="text-xs text-gray-700 list-disc pl-5 space-y-0.5">
                        {group.items.map((i) => (
                          <li key={i.id}>{i.name || i.id}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserLevelCouponPage;
